import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { formatInTimeZone } from "date-fns-tz";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CrewListDialog from "components/CrewListDialog";
import { hasPermission } from "state/checkPermission";
import { fetchLegMembersByFlt } from "state/api";


const ShuttleCard = ({ row, type }) => {
  const timeMode = useSelector((state) => state.timemode);
  const user = useSelector((state) => state.user);
  const canView = hasPermission(user.permissions, "CrewShuttles", "view", false);

  const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
  const [flight, setFlight] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [jumpList, setJumpList] = useState([]);

  const handleCrewListClick = async () => {
    if (!canView) return;
    try {
      const { flightDetails, jumpSeaters } =
        await fetchLegMembersByFlt(
          row.flightDate,
          row.flightDesc,
          row.dep
        );

      setFlight(flightDetails);
      setCrewList(row.crewMembers || []);
      setJumpList(jumpSeaters);
      setOpenCrewListDialog(true);
    } catch {
      setOpenCrewListDialog(false);
    }
  };
  
  const switchTime = useCallback(
    (utc, local) => (timeMode === "UTC" ? utc : local),
    [timeMode]
  );

  const time = type === "pickup"
    ? switchTime(row.fullPickup, row.fullPickupLocalBase)
    : switchTime(row.fullTakeBack, row.fullTakeBackLocalBase);

  const etdDate = timeMode === "UTC" 
    ? formatInTimeZone(row.fullEtd, "UTC", "dd/MM")
    : formatInTimeZone(row.fullEtdLocalBase, "UTC", "dd/MM")

  const dep = type === "pickup" ? row.pickupDep : row.takeBackDep;
  const arr = type === "pickup" ? row.pickupArr : row.takeBackArr;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        borderColor: "neutral.deep",
      }}
    >
      <CardContent>
        <Typography
          variant="subtitle1"
          color={type === "pickup" ? "primary" : "secondary"}
          sx={{
            cursor: canView ? "pointer" : "default",
            "&:hover": canView && { textDecoration: "underline" },
          }}
          onClick={handleCrewListClick}
        >
          #{row.no}. {row.flightDesc} {row.sector} {switchTime(row.etd, row.etdLocalBase)} {etdDate} {switchTime(row.eta, row.etaLocalBase)} | {row.flightReg || row.acType}
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Info
          label={type === "pickup" ? "Pickup Time" : "Take Back Time"}
          value={ time
            ? formatInTimeZone(time, "UTC", "HH:mm dd/MM/yyyy")
            : "--"
          }
        />

        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="body1" color="text.secondary">
            Route
          </Typography>

          <Typography
            variant="body1"
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            sx={{ maxWidth: 160, textAlign: "right" }}
          >
            {dep || "--"}
            <ArrowRightAltIcon fontSize="small" />
            {arr || "--"}
          </Typography>
        </Box>

        <Info label="Crew Count" value={row.crewCount} />
      </CardContent>

      {openCrewListDialog && (
        <CrewListDialog
          open={openCrewListDialog}
          onClose={() => setOpenCrewListDialog(false)}
          flight={flight}
          crewList={crewList}
          jumpList={jumpList}
          isLocked
        />
      )}
    </Card>
  );
};

const Info = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={0.5}>
    <Typography variant="body1" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value ?? "--"}</Typography>
  </Box>
);

export default ShuttleCard;
